/**
 * Trunnion AI - Execution Replay Store
 * Manages playback state for the VCR-style execution replay viewer.
 * Supports play/pause, step forward/back, seeking, and variable speed.
 */

import { create } from 'zustand';
import type { ExecutionStep } from '@/types';

export interface ReplayState {
  /** Loaded execution steps for replay */
  steps: ExecutionStep[];
  /** Index of the currently visible step */
  currentStepIndex: number;
  /** Whether auto-play is active */
  isPlaying: boolean;
  /** Playback speed multiplier (0.5, 1, 2, 4) */
  speed: number;

  // --- Actions ---

  /** Load a set of execution steps into the replay viewer */
  loadReplay: (steps: ExecutionStep[]) => void;
  /** Start auto-play */
  play: () => void;
  /** Pause auto-play */
  pause: () => void;
  /** Toggle between play and pause */
  togglePlay: () => void;
  /** Advance to the next step (clamped to bounds) */
  stepForward: () => void;
  /** Go back to the previous step (clamped to bounds) */
  stepBack: () => void;
  /** Jump to a specific step index */
  seekTo: (index: number) => void;
  /** Change the playback speed */
  setSpeed: (speed: number) => void;
  /** Reset the store to its initial state */
  reset: () => void;
}

export const useReplayStore = create<ReplayState>((set, get) => ({
  steps: [],
  currentStepIndex: 0,
  isPlaying: false,
  speed: 1,

  loadReplay: (steps) =>
    set({
      steps,
      currentStepIndex: 0,
      isPlaying: false,
    }),

  play: () => {
    const { steps, currentStepIndex } = get();
    // Don't start playing if we're already at the last step
    if (currentStepIndex >= steps.length - 1) {
      // Reset to the beginning and play
      set({ currentStepIndex: 0, isPlaying: true });
    } else {
      set({ isPlaying: true });
    }
  },

  pause: () => set({ isPlaying: false }),

  togglePlay: () => {
    const { isPlaying } = get();
    if (isPlaying) {
      get().pause();
    } else {
      get().play();
    }
  },

  stepForward: () => {
    const { steps, currentStepIndex } = get();
    if (currentStepIndex < steps.length - 1) {
      set({ currentStepIndex: currentStepIndex + 1 });
    } else {
      // Reached the end — stop playing
      set({ isPlaying: false });
    }
  },

  stepBack: () => {
    const { currentStepIndex } = get();
    if (currentStepIndex > 0) {
      set({ currentStepIndex: currentStepIndex - 1 });
    }
  },

  seekTo: (index) => {
    const { steps } = get();
    const clamped = Math.max(0, Math.min(index, steps.length - 1));
    set({ currentStepIndex: clamped });
  },

  setSpeed: (speed) => set({ speed }),

  reset: () =>
    set({
      steps: [],
      currentStepIndex: 0,
      isPlaying: false,
      speed: 1,
    }),
}));
